import { useEffect, useMemo, useState } from "react";
import { Loader2, Plus, Pencil, Trash2, X, Tag } from "lucide-react";
import { toast } from "sonner";
import { http, rupiah, apiError } from "@/lib/api";
import AdminShell, { PageHead } from "@/components/AdminShell";

const emptyForm = {
  code: "",
  name: "",
  discount_type: "percent",
  value: 10,
  min_order: 0,
  max_discount: 0,
  start_date: "",
  end_date: "",
  usage_limit: 0,
  per_customer_limit: 0,
  payment_methods: ["cash", "qris"],
  active: true,
};

function numberValue(v) {
  const n = Number(v);
  return Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0;
}

export default function Vouchers() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await http.get("/admin/vouchers");
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const activeCount = useMemo(
    () => rows.filter((v) => v.active).length,
    [rows]
  );

  function startCreate() {
    setEditing(null);
    setForm({ ...emptyForm, payment_methods: ["cash", "qris"] });
    setOpen(true);
  }

  function startEdit(v) {
    setEditing(v.id);
    setForm({
      code: v.code || "",
      name: v.name || "",
      discount_type: v.discount_type || "percent",
      value: Number(v.value || 0),
      min_order: Number(v.min_order || 0),
      max_discount: Number(v.max_discount || 0),
      start_date: v.start_date || "",
      end_date: v.end_date || "",
      usage_limit: Number(v.usage_limit || 0),
      per_customer_limit: Number(v.per_customer_limit || 0),
      payment_methods: Array.isArray(v.payment_methods) && v.payment_methods.length
        ? v.payment_methods
        : ["cash", "qris"],
      active: v.active !== false,
    });
    setOpen(true);
  }

  function togglePayment(method) {
    setForm((current) => {
      const has = current.payment_methods.includes(method);
      let next = has
        ? current.payment_methods.filter((x) => x !== method)
        : [...current.payment_methods, method];
      if (!next.length) next = [method];
      return { ...current, payment_methods: next };
    });
  }

  async function save() {
    const code = String(form.code || "").trim().toUpperCase();
    if (code.length < 3) {
      toast.error("Kode voucher minimal 3 karakter");
      return;
    }
    if (!form.name.trim()) {
      toast.error("Nama promo wajib diisi");
      return;
    }
    if (numberValue(form.value) <= 0) {
      toast.error("Nilai diskon harus lebih dari 0");
      return;
    }
    if (form.discount_type === "percent" && numberValue(form.value) > 100) {
      toast.error("Diskon persen maksimal 100%");
      return;
    }
    if (form.start_date && form.end_date && form.start_date > form.end_date) {
      toast.error("Tanggal mulai tidak boleh melewati tanggal berakhir");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        code,
        value: numberValue(form.value),
        min_order: numberValue(form.min_order),
        max_discount: numberValue(form.max_discount),
        usage_limit: numberValue(form.usage_limit),
        per_customer_limit: numberValue(form.per_customer_limit),
      };
      if (editing) {
        await http.patch(`/admin/vouchers/${editing}`, payload);
        toast.success("Voucher diperbarui");
      } else {
        await http.post("/admin/vouchers", payload);
        toast.success("Voucher dibuat");
      }
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  }

  async function remove(v) {
    if (!window.confirm(`Hapus voucher ${v.code}?`)) return;
    try {
      await http.delete(`/admin/vouchers/${v.id}`);
      toast.success("Voucher dihapus");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  }

  async function toggleActive(v) {
    try {
      await http.patch(`/admin/vouchers/${v.id}`, { active: !v.active });
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  }

  return (
    <AdminShell>
      <div>
        <PageHead
        eyebrow="PROMO"
        title="Promo & Voucher"
        desc={`${activeCount} voucher aktif · Buat kode diskon untuk checkout customer.`}
      >
        <button
          type="button"
          onClick={startCreate}
          className="flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground"
        >
          <Plus size={16} /> Buat Voucher
        </button>
      </PageHead>

      {loading ? (
        <div className="grid min-h-52 place-items-center">
          <Loader2 className="wm-spin text-muted-foreground" />
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-border bg-card p-10 text-center">
          <Tag className="mx-auto text-muted-foreground" size={34} />
          <div className="mt-3 font-heading text-lg font-extrabold">Belum ada voucher</div>
          <p className="mt-1 text-sm text-muted-foreground">Buat voucher pertama untuk customer.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-3xl border border-border bg-card">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-left text-sm">
              <thead className="bg-muted text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Kode</th>
                  <th className="px-4 py-3">Diskon</th>
                  <th className="px-4 py-3">Min. Belanja</th>
                  <th className="px-4 py-3">Periode</th>
                  <th className="px-4 py-3">Pemakaian</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((v) => (
                  <tr key={v.id} className="border-t border-border">
                    <td className="px-4 py-3">
                      <div className="font-heading font-extrabold text-card-foreground">{v.code}</div>
                      <div className="text-xs text-muted-foreground">{v.name}</div>
                    </td>
                    <td className="px-4 py-3 font-bold">
                      {v.discount_type === "percent"
                        ? `${v.value}%${v.max_discount ? ` · maks ${rupiah(v.max_discount)}` : ""}`
                        : rupiah(v.value)}
                    </td>
                    <td className="px-4 py-3">{v.min_order ? rupiah(v.min_order) : "Tanpa minimum"}</td>
                    <td className="px-4 py-3 text-xs">
                      <div>{v.start_date || "Sekarang"}</div>
                      <div className="text-muted-foreground">s/d {v.end_date || "Tanpa batas"}</div>
                    </td>
                    <td className="px-4 py-3">
                      <b>{v.usage_count || 0}</b>
                      <span className="text-muted-foreground"> / {v.usage_limit || "∞"}</span>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => toggleActive(v)}
                        className={`rounded-full px-2.5 py-1 text-xs font-bold ${
                          v.active
                            ? "bg-emerald-500/15 text-emerald-500"
                            : "bg-muted text-muted-foreground"
                        }`}
                      >
                        {v.active ? "Aktif" : "Nonaktif"}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => startEdit(v)}
                          className="grid h-9 w-9 place-items-center rounded-xl bg-muted"
                          aria-label="Edit"
                        >
                          <Pencil size={15} />
                        </button>
                        <button
                          type="button"
                          onClick={() => remove(v)}
                          className="grid h-9 w-9 place-items-center rounded-xl bg-red-500/10 text-red-500"
                          aria-label="Hapus"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/60 p-0 backdrop-blur-sm sm:items-center sm:p-5">
          <div className="max-h-[92dvh] w-full max-w-2xl overflow-y-auto rounded-t-3xl border border-border bg-card p-5 sm:rounded-3xl">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-heading text-2xl font-extrabold">
                  {editing ? "Edit Voucher" : "Buat Voucher"}
                </h2>
                <p className="text-sm text-muted-foreground">Atur diskon dan batas pemakaian.</p>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="grid h-10 w-10 place-items-center rounded-xl bg-muted">
                <X size={18} />
              </button>
            </div>

            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <Field label="Kode Voucher">
                <input
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, "") })}
                  placeholder="NGEBLAR10"
                  maxLength={30}
                  className="input-voucher"
                />
              </Field>

              <Field label="Nama Promo">
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Promo Oktober"
                  maxLength={80}
                  className="input-voucher"
                />
              </Field>

              <Field label="Jenis Diskon">
                <select
                  value={form.discount_type}
                  onChange={(e) => setForm({ ...form, discount_type: e.target.value })}
                  className="input-voucher"
                >
                  <option value="percent">Persen (%)</option>
                  <option value="fixed">Nominal (Rp)</option>
                </select>
              </Field>

              <Field label={form.discount_type === "percent" ? "Nilai Diskon (%)" : "Nilai Diskon (Rp)"}>
                <input
                  type="number"
                  min="0"
                  max={form.discount_type === "percent" ? "100" : undefined}
                  value={form.value}
                  onChange={(e) => setForm({ ...form, value: e.target.value })}
                  className="input-voucher"
                />
              </Field>

              <Field label="Minimum Belanja (Rp)">
                <input type="number" min="0" value={form.min_order} onChange={(e) => setForm({ ...form, min_order: e.target.value })} className="input-voucher" />
              </Field>

              <Field label="Maksimum Potongan (Rp)">
                <input
                  type="number"
                  min="0"
                  value={form.max_discount}
                  disabled={form.discount_type !== "percent"}
                  onChange={(e) => setForm({ ...form, max_discount: e.target.value })}
                  className="input-voucher disabled:opacity-40"
                />
                <div className="mt-1 text-[11px] text-muted-foreground">0 = tanpa batas. Dipakai untuk diskon persen.</div>
              </Field>

              <Field label="Mulai Berlaku">
                <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="input-voucher" />
              </Field>

              <Field label="Berakhir">
                <input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="input-voucher" />
              </Field>

              <Field label="Limit Total Pemakaian">
                <input type="number" min="0" value={form.usage_limit} onChange={(e) => setForm({ ...form, usage_limit: e.target.value })} className="input-voucher" />
                <div className="mt-1 text-[11px] text-muted-foreground">0 = unlimited.</div>
              </Field>

              <Field label="Limit per Customer">
                <input type="number" min="0" value={form.per_customer_limit} onChange={(e) => setForm({ ...form, per_customer_limit: e.target.value })} className="input-voucher" />
                <div className="mt-1 text-[11px] text-muted-foreground">0 = unlimited.</div>
              </Field>
            </div>

            <div className="mt-4">
              <div className="mb-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">Metode Pembayaran</div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  ["cash", "💵 Cash"],
                  ["qris", "📱 QRIS"],
                ].map(([value, label]) => {
                  const active = form.payment_methods.includes(value);
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => togglePayment(value)}
                      className={`rounded-2xl border-2 p-3 text-left text-sm font-bold ${
                        active ? "border-primary bg-primary/5" : "border-border bg-muted"
                      }`}
                    >
                      {label} {active ? "✓" : ""}
                    </button>
                  );
                })}
              </div>
            </div>

            <label className="mt-4 flex items-center gap-3 rounded-2xl bg-muted p-3">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
              />
              <span className="text-sm font-bold">Voucher aktif</span>
            </label>

            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="mt-5 flex w-full items-center justify-center gap-2 rounded-2xl bg-primary py-3.5 font-bold text-primary-foreground disabled:opacity-50"
            >
              {saving && <Loader2 className="wm-spin" size={17} />}
              {editing ? "Simpan Perubahan" : "Buat Voucher"}
            </button>

            <style>{`.input-voucher{width:100%;border:1px solid var(--border);background:var(--muted);border-radius:1rem;padding:.8rem .9rem;font-size:.875rem;outline:none;color:var(--foreground)}`}</style>
          </div>
        </div>
      )}
      </div>
    </AdminShell>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="mb-1.5 text-xs font-bold uppercase tracking-wide text-muted-foreground">{label}</div>
      {children}
    </label>
  );
}
